import type {
  CreateQCStandardFormDTO,
  UpdateQCStandardFormDTO,
} from "@/modules/qc/domain/contracts";
import type {
  CreateQCRecordDTO as CreateQCRecordFormDTO,
  UpdateQCRecordDTO as UpdateQCRecordFormDTO,
} from "@/modules/qc/domain/contracts/record";
import type { QCResultStatsQueryDTO } from "@/modules/qc/domain/contracts/result";
import type {
  CreateWorkItemTypeDTO,
  UpdateWorkItemTypeDTO,
  WorkItemTypeDTO,
} from "@/modules/qc/domain/contracts/work-item-type";
import type {
  CreateQCWorkTypeDTO,
  QCWorkTypeDTO,
  UpdateQCWorkTypeDTO,
} from "@/modules/qc/domain/contracts/work-type";
import type { WorkItemTypesListResult } from "@/modules/qc/domain/repo/work-item-type/list-work-item-types";
import type { QCWorkTypesListResult } from "@/modules/qc/domain/repo/work-type/list-work-types";
import type {
  QCStandardByIdResult,
  QCStandardsListResult,
} from "@/modules/qc/domain/types";
import type {
  QCRecordByIdResult,
  QCRecordsListResult,
} from "@/modules/qc/domain/types/record";
import type { QCResultStatsResult } from "@/modules/qc/domain/types/result";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import { fetchLookup, hydrateLookupItem } from "@/shared/lib/utils";

const baseUrl = `${config.apiUrl}/qc`;

export const qcApi = {
  async list(
    query: Partial<OffsetPageQueryDTO> = {},
  ): Promise<QCStandardsListResult> {
    const url = new URL(baseUrl);
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    if (query.offset) url.searchParams.set("offset", String(query.offset));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<QCStandardsListResult>(url.toString());
  },

  async get(id: string): Promise<QCStandardByIdResult> {
    return fetcher.get<QCStandardByIdResult>(`${baseUrl}/${id}`);
  },

  async create(input: CreateQCStandardFormDTO): Promise<QCStandardByIdResult> {
    return fetcher.post<QCStandardByIdResult>(baseUrl, input);
  },

  async update(
    id: string,
    input: UpdateQCStandardFormDTO,
  ): Promise<QCStandardByIdResult> {
    return fetcher.put<QCStandardByIdResult>(`${baseUrl}/${id}`, input);
  },

  async delete(id: string): Promise<{ ok: true }> {
    return fetcher.delete<{ ok: true }>(`${baseUrl}/${id}`);
  },
};

export const qcWorkTypesApi = {
  async list(query: OffsetPageQueryDTO): Promise<QCWorkTypesListResult> {
    const url = new URL(`${baseUrl}/work-types`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<QCWorkTypesListResult>(url.toString());
  },

  async get(id: string) {
    return fetcher.get<QCWorkTypeDTO>(`${baseUrl}/work-types/${id}`);
  },

  async create(input: CreateQCWorkTypeDTO) {
    return fetcher.post<QCWorkTypeDTO>(`${baseUrl}/work-types`, input);
  },

  async update(id: string, input: UpdateQCWorkTypeDTO) {
    return fetcher.patch<QCWorkTypeDTO>(`${baseUrl}/work-types/${id}`, input);
  },

  async delete(id: string) {
    return fetcher.delete<{ ok: true }>(`${baseUrl}/work-types/${id}`);
  },

  async lookup(params: {
    query: string;
    cursor?: string | null;
    pageSize: number;
  }) {
    return fetchLookup(`${baseUrl}/work-types/lookup`, params);
  },

  async hydrate(id: string) {
    return hydrateLookupItem(`${baseUrl}/work-types/lookup`, id);
  },
};

export const qcRecordApi = {
  async list(
    query: Partial<OffsetPageQueryDTO> = {},
  ): Promise<QCRecordsListResult> {
    const url = new URL(`${baseUrl}/records`);
    if (query.limit) url.searchParams.set("limit", String(query.limit));
    if (query.offset) url.searchParams.set("offset", String(query.offset));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<QCRecordsListResult>(url.toString());
  },

  async get(id: string): Promise<QCRecordByIdResult> {
    return fetcher.get<QCRecordByIdResult>(`${baseUrl}/records/${id}`);
  },

  async create(input: CreateQCRecordFormDTO): Promise<QCRecordByIdResult> {
    return fetcher.post<QCRecordByIdResult>(`${baseUrl}/records`, input);
  },

  async update(
    id: string,
    input: UpdateQCRecordFormDTO,
  ): Promise<QCRecordByIdResult> {
    return fetcher.put<QCRecordByIdResult>(`${baseUrl}/records/${id}`, input);
  },

  async delete(id: string): Promise<{ ok: true }> {
    return fetcher.delete<{ ok: true }>(`${baseUrl}/records/${id}`);
  },
};

export const qcWorkItemTypesApi = {
  async list(query: OffsetPageQueryDTO): Promise<WorkItemTypesListResult> {
    const url = new URL(`${baseUrl}/work-item-types`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<WorkItemTypesListResult>(url.toString());
  },

  async get(id: string) {
    return fetcher.get<WorkItemTypeDTO>(`${baseUrl}/work-item-types/${id}`);
  },

  async create(input: CreateWorkItemTypeDTO) {
    return fetcher.post<WorkItemTypeDTO>(`${baseUrl}/work-item-types`, input);
  },

  async update(id: string, input: UpdateWorkItemTypeDTO) {
    return fetcher.patch<WorkItemTypeDTO>(
      `${baseUrl}/work-item-types/${id}`,
      input,
    );
  },

  async delete(id: string) {
    return fetcher.delete<{ ok: true }>(`${baseUrl}/work-item-types/${id}`);
  },
};

export const qcResultApi = {
  async getStats(
    query: Partial<QCResultStatsQueryDTO> = {},
  ): Promise<QCResultStatsResult> {
    const url = new URL(`${baseUrl}/records/stats`);
    if (query.departmentId)
      url.searchParams.set("departmentId", query.departmentId);
    if (query.qcStandardId)
      url.searchParams.set("qcStandardId", query.qcStandardId);
    if (query.startDate) url.searchParams.set("startDate", query.startDate);
    if (query.endDate) url.searchParams.set("endDate", query.endDate);
    if (query.workItemTypeId)
      url.searchParams.set("workItemTypeId", query.workItemTypeId);
    return fetcher.get<QCResultStatsResult>(url.toString());
  },
};
